import React, { FormEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PhoneInput from '../form/PhoneInput';
import { getOrCreateReferralDeviceId } from '../../utils/referralDevice';
import { applyContentLocaleToUrl } from '../../utils/contentLocaleQuery';
import { requiresStateOrProvince } from '../../utils/shippingAddress';
import {
  authLabelClass,
  authPrimaryButtonClass,
  authSecondaryButtonClass,
} from './authFormStyles';

function sanitizeReferralCode(raw: string): string | undefined {
  const s = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);
  return s || undefined;
}

type RegisterFormProps = {
  onRegistered?: (email: string) => void;
  onError: (message: string) => void;
  tone?: 'customer' | 'employee';
};

function RegisterForm({ onRegistered, onError, tone = 'customer' }: RegisterFormProps) {
  const { register } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const inviteCodeFromUrl = useMemo(
    () => sanitizeReferralCode(searchParams.get('ref') ?? ''),
    [searchParams],
  );
  const [inviteCodeInput, setInviteCodeInput] = React.useState('');
  const [referralConfig, setReferralConfig] = React.useState<{ enabled: boolean } | null>(
    null,
  );
  const [applyReferral, setApplyReferral] = React.useState(false);

  const referralCodeToSubmit = useMemo(() => {
    if (!referralConfig?.enabled || !applyReferral) return undefined;
    return sanitizeReferralCode(inviteCodeInput) || inviteCodeFromUrl;
  }, [referralConfig?.enabled, applyReferral, inviteCodeInput, inviteCodeFromUrl]);

  const referralCodeForIntent = useMemo(() => {
    if (!referralConfig?.enabled) return undefined;
    const fromInput = sanitizeReferralCode(inviteCodeInput);
    return inviteCodeFromUrl || fromInput;
  }, [referralConfig?.enabled, inviteCodeFromUrl, inviteCodeInput]);

  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          applyContentLocaleToUrl(
            `${API_BASE}/referrals/public-config`,
            navigator.language,
          ),
        );
        if (!res.ok) throw new Error('load');
        const data = (await res.json()) as { enabled?: boolean };
        if (!cancelled) {
          setReferralConfig({ enabled: Boolean(data.enabled) });
        }
      } catch {
        if (!cancelled) setReferralConfig({ enabled: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API_BASE]);

  React.useEffect(() => {
    if (!referralConfig?.enabled) return;
    if (inviteCodeFromUrl) setApplyReferral(true);
  }, [referralConfig?.enabled, inviteCodeFromUrl]);

  /** Lets registration finish after the program is turned off (server-side signup intent, 48h). */
  React.useEffect(() => {
    if (!referralConfig?.enabled || !referralCodeForIntent || referralCodeForIntent.length < 4) return;
    const timer = window.setTimeout(() => {
      const deviceId = getOrCreateReferralDeviceId();
      void fetch(
        applyContentLocaleToUrl(
          `${API_BASE}/referrals/public/claim-signup-intent`,
          navigator.language,
        ),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            referralCode: referralCodeForIntent,
            referralDeviceId: deviceId,
          }),
        },
      ).catch(() => {});
    }, 450);
    return () => window.clearTimeout(timer);
  }, [API_BASE, referralConfig?.enabled, referralCodeForIntent]);

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [fullName, setFullName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [countryInput, setCountryInput] = React.useState('');
  const [countryCode, setCountryCode] = React.useState('');
  const [cityInput, setCityInput] = React.useState('');
  const [street, setStreet] = React.useState('');
  const [street2, setStreet2] = React.useState('');
  const [stateOrProvince, setStateOrProvince] = React.useState('');
  const [postalCode, setPostalCode] = React.useState('');
  const [addressSearch, setAddressSearch] = React.useState('');
  const [latitude, setLatitude] = React.useState<number | undefined>(undefined);
  const [longitude, setLongitude] = React.useState<number | undefined>(undefined);
  const [countrySuggestions, setCountrySuggestions] = React.useState<Array<{ name: string; code?: string }>>([]);
  const [citySuggestions, setCitySuggestions] = React.useState<Array<{ name: string }>>([]);
  const [addressSuggestions, setAddressSuggestions] = React.useState<
    Array<{
      label: string;
      street: string;
      street2?: string;
      city: string;
      stateOrProvince?: string;
      country: string;
      countryCode?: string;
      postalCode: string;
      latitude?: number;
      longitude?: number;
    }>
  >([]);
  const [countrySelected, setCountrySelected] = React.useState(false);
  const [citySelected, setCitySelected] = React.useState(false);
  const [addressSelected, setAddressSelected] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [verificationSent, setVerificationSent] = React.useState(false);
  const [detectingCountry, setDetectingCountry] = React.useState(false);
  const [countryDetectError, setCountryDetectError] = React.useState('');
  const [step, setStep] = React.useState<1 | 2>(1);
  const countryCacheRef = React.useRef(new Map<string, Array<{ name: string; code?: string }>>());
  const cityCacheRef = React.useRef(new Map<string, Array<{ name: string }>>());
  const addressCacheRef = React.useRef(new Map<string, Array<{
    label: string;
    street: string;
    street2?: string;
    city: string;
    stateOrProvince?: string;
    country: string;
    countryCode?: string;
    postalCode: string;
    latitude?: number;
    longitude?: number;
  }>>());

  const needsState = requiresStateOrProvince(countryCode);

  React.useEffect(() => {
    let cancelled = false;
    const q = countryInput.trim();
    if (q.length < 2 || countrySelected) {
      setCountrySuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      const key = q.toLowerCase();
      const cached = countryCacheRef.current.get(key);
      if (cached) {
        setCountrySuggestions(cached);
        return;
      }
      void (async () => {
        const res = await fetch(`${API_BASE}/auth/location/countries?q=${encodeURIComponent(q)}`);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ name: string; code?: string }>;
        if (!cancelled) {
          countryCacheRef.current.set(key, rows);
          setCountrySuggestions(rows);
        }
      })();
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [countryInput, countrySelected, API_BASE]);

  React.useEffect(() => {
    let cancelled = false;
    const q = cityInput.trim();
    if (!countrySelected || q.length < 2 || citySelected) {
      setCitySuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      const key = `${countryInput.toLowerCase()}:${countryCode.toLowerCase()}:${q.toLowerCase()}`;
      const cached = cityCacheRef.current.get(key);
      if (cached) {
        setCitySuggestions(cached);
        return;
      }
      void (async () => {
        const url = `${API_BASE}/auth/location/cities?country=${encodeURIComponent(countryInput)}&countryCode=${encodeURIComponent(countryCode)}&q=${encodeURIComponent(q)}`;
        const res = await fetch(url);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ name: string }>;
        if (!cancelled) {
          cityCacheRef.current.set(key, rows);
          setCitySuggestions(rows);
        }
      })();
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cityInput, citySelected, countryInput, countryCode, countrySelected, API_BASE]);

  React.useEffect(() => {
    let cancelled = false;
    const q = addressSearch.trim();
    if (!countrySelected || q.length < 3 || addressSelected) {
      setAddressSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      const key = `${countryInput.toLowerCase()}:${countryCode.toLowerCase()}:${cityInput.toLowerCase()}:${q.toLowerCase()}`;
      const cached = addressCacheRef.current.get(key);
      if (cached) {
        setAddressSuggestions(cached);
        return;
      }
      void (async () => {
        const url = `${API_BASE}/auth/location/addresses?country=${encodeURIComponent(countryInput)}&countryCode=${encodeURIComponent(countryCode)}&city=${encodeURIComponent(cityInput)}&q=${encodeURIComponent(q)}`;
        const res = await fetch(url);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{
          label: string;
          street: string;
          street2?: string;
          city: string;
          stateOrProvince?: string;
          country: string;
          countryCode?: string;
          postalCode: string;
          latitude?: number;
          longitude?: number;
        }>;
        if (!cancelled) {
          addressCacheRef.current.set(key, rows);
          setAddressSuggestions(rows);
        }
      })();
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [addressSearch, addressSelected, cityInput, countryInput, countryCode, countrySelected, API_BASE]);

  const detectCountryFromBrowserLocation = async () => {
    if (detectingCountry) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setCountryDetectError(
        t('auth.geo_not_supported', {
          defaultValue: 'Geolocation is not supported in this browser.',
        }),
      );
      return;
    }

    setCountryDetectError('');
    setDetectingCountry(true);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 12000,
          maximumAge: 60000,
        });
      });

      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const url = `${API_BASE}/auth/location/detect-country?lat=${encodeURIComponent(
        String(lat),
      )}&lng=${encodeURIComponent(String(lng))}`;

      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(
          t('auth.detect_country_failed', {
            defaultValue: 'Unable to detect country.',
          }),
        );
      }
      const data = (await res.json()) as
        | { country?: string; countryCode?: string; reason?: string }
        | null;
      const country = data?.country ? String(data.country).trim() : '';
      const detectedCode = data?.countryCode ? String(data.countryCode).trim() : '';

      if (!country) {
        const reason = data?.reason ? String(data.reason).trim() : '';
        setCountryDetectError(
          reason
            ? `Could not determine your country from your location. ${reason}`
            : t('auth.detect_country_unknown', {
                defaultValue: 'Could not determine your country from your location.',
              }),
        );
        return;
      }

      // Prefill country and reset lower fields so the user can select the matching city/address.
      setCountryInput(country);
      setCountryCode(detectedCode);
      setCountrySelected(true);
      setCountrySuggestions([]);
      setCityInput('');
      setCitySelected(false);
      setAddressSearch('');
      setAddressSelected(false);
      setStreet('');
      setStreet2('');
      setStateOrProvince('');
      setPostalCode('');
      setLatitude(undefined);
      setLongitude(undefined);
    } catch (err) {
      setCountryDetectError(
        err instanceof Error
          ? err.message
          : t('auth.geo_permission_failed', {
              defaultValue: 'Could not get location permission.',
            }),
      );
    } finally {
      setDetectingCountry(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting || verificationSent) return;
    if (!fullName.trim() || !phone.trim() || !email.trim() || !password.trim()) {
      onError(
        t('auth.register_complete_account_details', {
          defaultValue: 'Please complete your account details first.',
        }),
      );
      return;
    }
    if (password !== confirmPassword) {
      onError(
        t('auth.register_password_mismatch', {
          defaultValue: 'Password confirmation does not match.',
        }),
      );
      return;
    }
    if (!countryInput.trim() || !cityInput.trim() || !street.trim() || !postalCode.trim()) {
      onError(
        t('auth.register_complete_delivery_address', {
          defaultValue: 'Please complete your delivery address.',
        }),
      );
      return;
    }
    if (needsState && !stateOrProvince.trim()) {
      onError(
        t('auth.state_required_hint', {
          defaultValue: 'State or province is required for shipping to your country.',
        }),
      );
      return;
    }

    if (referralConfig?.enabled && applyReferral && !referralCodeToSubmit) {
      onError(t('auth.register_referral_required_when_checked'));
      return;
    }

    setSubmitting(true);
    onError('');

    try {
      const addressSource: 'MANUAL' | 'SUGGESTION' = addressSelected
        ? 'SUGGESTION'
        : 'MANUAL';
      await register({
        email,
        password,
        fullName,
        phone,
        country: countryInput,
        countryCode: countryCode || undefined,
        city: cityInput,
        street,
        street2: street2.trim() || undefined,
        stateOrProvince: stateOrProvince.trim() || undefined,
        postalCode,
        latitude,
        longitude,
        addressSource,
        referralCode: referralCodeToSubmit,
        referralDeviceId: getOrCreateReferralDeviceId(),
      });
      setVerificationSent(true);
      if (onRegistered) {
        onRegistered(email);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('auth.register_unable_create', {
              defaultValue: 'Unable to create account.',
            });
      onError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <fieldset
        disabled={submitting || verificationSent}
        className="space-y-4 disabled:opacity-70"
      >
      <div className="mb-4 flex items-center gap-3 border-b border-slate-200 pb-4">
        <div
          className={`flex h-7 w-7 items-center justify-center rounded-sm text-xs font-semibold ${
            step >= 1 ? 'bg-slate-900 text-white' : 'border border-slate-300 text-slate-500'
          }`}
        >
          1
        </div>
        <div className={`h-px flex-1 ${step >= 2 ? 'bg-slate-900' : 'bg-slate-200'}`} />
        <div
          className={`flex h-7 w-7 items-center justify-center rounded-sm text-xs font-semibold ${
            step >= 2 ? 'bg-slate-900 text-white' : 'border border-slate-300 text-slate-500'
          }`}
        >
          2
        </div>
      </div>

      {tone === 'customer' && referralConfig?.enabled && applyReferral && referralCodeToSubmit ? (
        <div className="mb-3 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs leading-relaxed text-emerald-900">
          {t('auth.register_invite_applied', { code: referralCodeToSubmit })}
        </div>
      ) : null}

      {step === 1 ? (
        <>
      <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-1.5 text-sm">
        <label
          htmlFor="register-full-name"
          className={authLabelClass('light')}
        >
          {t('auth.full_name_label', { defaultValue: 'Full name' })}
        </label>
        <input
          id="register-full-name"
          name="fullName"
          type="text"
          className={[
            'w-full rounded-sm border bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2',
            tone === 'employee'
              ? 'border-slate-300 focus:border-slate-700 focus:ring-slate-300/70'
              : 'border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900',
          ].join(' ')}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
        />
      </div>
      <PhoneInput
        id="register-phone"
        label={t('auth.phone_label', { defaultValue: 'Phone' })}
        value={phone}
        onChange={setPhone}
      />
      </div>

      {tone === 'customer' && referralConfig?.enabled ? (
        <div className="space-y-2 text-sm">
          <label className="flex cursor-pointer items-start gap-3 rounded-sm border border-slate-200 bg-slate-50 p-3.5 transition hover:border-emerald-200/80">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-[#00b853] focus:ring-brand-500"
              checked={applyReferral}
              onChange={(e) => {
                const on = e.target.checked;
                setApplyReferral(on);
                if (!on) setInviteCodeInput('');
              }}
            />
            <span className="text-sm font-medium leading-snug text-slate-800">
              {t('auth.register_apply_referral_checkbox')}
            </span>
          </label>
          {applyReferral ? (
            <div className="space-y-1.5 pl-0.5 sm:pl-1">
              <label
                htmlFor="register-invite-code"
                className={authLabelClass('light')}
              >
                {t('auth.register_invite_code_label')}
              </label>
              <input
                id="register-invite-code"
                name="referralCode"
                type="text"
                inputMode="text"
                autoComplete="off"
                maxLength={16}
                className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2 font-mono text-sm uppercase tracking-wide text-slate-900 outline-none placeholder:font-sans placeholder:normal-case placeholder:tracking-normal placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                placeholder={t('auth.register_invite_code_placeholder')}
                value={inviteCodeInput}
                onChange={(e) =>
                  setInviteCodeInput(
                    e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16),
                  )
                }
              />
              <p className="text-[11px] leading-snug text-slate-500">
                {t('auth.register_invite_code_hint')}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-1.5 text-sm">
        <label htmlFor="register-email" className={authLabelClass('light')}>
          {t('auth.email_label')}
        </label>
        <input
          id="register-email"
          name="email"
          type="email"
          autoComplete="email"
          className={[
            'w-full rounded-sm border bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2',
            tone === 'employee'
              ? 'border-slate-300 focus:border-slate-700 focus:ring-slate-300/70'
              : 'border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900',
          ].join(' ')}
          placeholder={t('auth.email_placeholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="space-y-1.5 text-sm">
        <label htmlFor="register-password" className={authLabelClass('light')}>
          {t('auth.password_label')}
        </label>
        <div className="relative">
          <input
            id="register-password"
            name="new-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            className={[
              'w-full rounded-sm border bg-white px-3 py-2 pr-16 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2',
              tone === 'employee'
                ? 'border-slate-300 focus:border-slate-700 focus:ring-slate-300/70'
                : 'border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900',
            ].join(' ')}
            placeholder={t('auth.password_placeholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute inset-y-0 right-2 flex items-center text-xs text-slate-500 hover:text-slate-700"
            aria-label={showPassword ? t('employeeInvite.hide_password', 'Hide password') : t('employeeInvite.show_password', 'Show password')}
          >
            <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
          </button>
        </div>
      </div>

      <div className="space-y-1.5 text-sm">
        <label htmlFor="register-password-confirm" className={authLabelClass('light')}>
          {t('auth.confirm_password_label', {
            defaultValue: 'Confirm password',
          })}
        </label>
        <div className="relative">
          <input
            id="register-password-confirm"
            name="new-password-confirm"
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            className={[
              'w-full rounded-sm border bg-white px-3 py-2 pr-16 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2',
              tone === 'employee'
                ? 'border-slate-300 focus:border-slate-700 focus:ring-slate-300/70'
                : 'border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900',
            ].join(' ')}
            placeholder={t('auth.confirm_password_placeholder', {
              defaultValue: 'Repeat your password',
            })}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword((v) => !v)}
            className="absolute inset-y-0 right-2 flex items-center text-xs text-slate-500 hover:text-slate-700"
            aria-label={showConfirmPassword ? t('employeeInvite.hide_password', 'Hide password') : t('employeeInvite.show_password', 'Show password')}
          >
            <i className={`fa ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
          </button>
        </div>
      </div>

      <div className="pt-1">
        <button
          type="button"
          onClick={() => {
            if (!fullName.trim() || !phone.trim() || !email.trim() || !password.trim() || !confirmPassword.trim()) {
              onError(
                t('auth.register_complete_account_details', {
                  defaultValue: 'Please complete your account details first.',
                }),
              );
              return;
            }
            if (password !== confirmPassword) {
              onError(
                t('auth.register_password_mismatch', {
                  defaultValue: 'Password confirmation does not match.',
                }),
              );
              return;
            }
            onError('');
            setStep(2);
          }}
          className="inline-flex w-full items-center justify-center rounded-sm bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
        >
          {t('auth.register_continue_delivery', {
            defaultValue: 'Continue to delivery address',
          })}
        </button>
      </div>
        </>
      ) : (
        <>
      <div className="rounded-sm border border-slate-200 bg-slate-50/80 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">
          {t('auth.delivery_address_title', { defaultValue: 'Delivery address' })}
        </h3>
        <p className="mt-1 text-xs leading-relaxed text-slate-600">
          {t('auth.delivery_address_subtitle', {
            defaultValue:
              'Enter your address exactly as it should appear on shipping labels. FedEx and other carriers use these fields to validate delivery.',
          })}
        </p>
      </div>

      <div className="space-y-1.5 text-sm">
        <label
          htmlFor="register-country"
          className={authLabelClass('light')}
        >
          {t('auth.country_region_label', { defaultValue: 'Country / region' })}
        </label>
        <div className="flex items-center gap-2">
          <input
            id="register-country"
            name="country"
            type="text"
            autoComplete="country-name"
            className={[
              'flex-1 rounded-sm border bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2',
              tone === 'employee'
                ? 'border-slate-300 focus:border-slate-700 focus:ring-slate-300/70'
                : 'border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900',
            ].join(' ')}
            placeholder={t('auth.country_search_select', {
              defaultValue: 'Search and select your country',
            })}
            value={countryInput}
            onChange={(e) => {
              setCountryInput(e.target.value);
              setCountryCode('');
              setCountrySelected(false);
              setCityInput('');
              setCitySelected(false);
              setAddressSearch('');
              setAddressSelected(false);
              setStreet('');
              setStreet2('');
              setStateOrProvince('');
              setPostalCode('');
              setLatitude(undefined);
              setLongitude(undefined);
            }}
            required
          />
          {tone !== 'employee' ? (
            <button
              type="button"
              onClick={() => void detectCountryFromBrowserLocation()}
              disabled={detectingCountry}
              className="inline-flex w-auto shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-sm border border-slate-900 bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M12 21s7-4.35 7-11a7 7 0 1 0-14 0c0 6.65 7 11 7 11Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 10.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
              </svg>
              {detectingCountry
                ? t('auth.detecting_location', { defaultValue: 'Detecting…' })
                : t('auth.use_my_location', { defaultValue: 'Use my location' })}
            </button>
          ) : null}
        </div>
        {countryCode ? (
          <p className="text-[11px] text-slate-500">ISO: {countryCode}</p>
        ) : null}
        {countryDetectError ? (
          <div className="pt-1 text-[11px] font-medium text-red-600">{countryDetectError}</div>
        ) : null}
        {countrySuggestions.length > 0 ? (
          <div className="max-h-32 overflow-auto rounded-sm border border-slate-200 bg-white">
            {countrySuggestions.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={() => {
                  setCountryInput(c.name);
                  setCountryCode(c.code || '');
                  setCountrySelected(true);
                  setCountrySuggestions([]);
                }}
                className="block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
              >
                {c.name}
                {c.code ? <span className="ml-2 text-slate-400">{c.code}</span> : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="space-y-1.5 text-sm">
        <label htmlFor="register-address-search" className={authLabelClass('light')}>
          {t('auth.address_search_label', { defaultValue: 'Find my address (optional)' })}
        </label>
        <input
          id="register-address-search"
          name="addressSearch"
          type="text"
          disabled={!countrySelected}
          className={[
            'w-full rounded-sm border bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-100',
            tone === 'employee'
              ? 'border-slate-300 focus:border-slate-700 focus:ring-slate-300/70'
              : 'border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900',
          ].join(' ')}
          placeholder={t('auth.address_search_placeholder', {
            defaultValue: 'Start typing your address…',
          })}
          value={addressSearch}
          onChange={(e) => {
            setAddressSearch(e.target.value);
            setAddressSelected(false);
          }}
        />
        <p className="text-[11px] leading-snug text-slate-500">
          {t('auth.address_search_hint', {
            defaultValue:
              'Select a suggestion to fill the fields below, or enter your address manually.',
          })}
        </p>
        {addressSuggestions.length > 0 ? (
          <div className="max-h-40 overflow-auto rounded-sm border border-slate-200 bg-white">
            {addressSuggestions.map((a) => (
              <button
                key={`${a.label}-${a.latitude}-${a.longitude}`}
                type="button"
                onClick={() => {
                  setAddressSearch(a.label);
                  setAddressSelected(true);
                  setAddressSuggestions([]);
                  setStreet(a.street || a.label);
                  setStreet2(a.street2 || '');
                  setPostalCode(a.postalCode || '');
                  setCityInput(a.city || cityInput);
                  setStateOrProvince(a.stateOrProvince || '');
                  setCountryInput(a.country || countryInput);
                  setCountryCode(a.countryCode || countryCode);
                  setLatitude(a.latitude);
                  setLongitude(a.longitude);
                  if (a.city) setCitySelected(true);
                  if (a.country) setCountrySelected(true);
                }}
                className="block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
              >
                {a.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="space-y-1.5 text-sm">
        <label htmlFor="register-street" className={authLabelClass('light')}>
          {t('auth.street_line1_label', { defaultValue: 'Street address' })}
        </label>
        <input
          id="register-street"
          name="street"
          type="text"
          autoComplete="address-line1"
          className={[
            'w-full rounded-sm border bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2',
            tone === 'employee'
              ? 'border-slate-300 focus:border-slate-700 focus:ring-slate-300/70'
              : 'border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900',
          ].join(' ')}
          placeholder={t('auth.street_line1_placeholder', {
            defaultValue: 'House number and street name',
          })}
          value={street}
          onChange={(e) => {
            setStreet(e.target.value);
            setAddressSelected(false);
          }}
          required
        />
      </div>

      <div className="space-y-1.5 text-sm">
        <label htmlFor="register-street2" className={authLabelClass('light')}>
          {t('auth.street_line2_label', {
            defaultValue: 'Apartment, suite, unit (optional)',
          })}
        </label>
        <input
          id="register-street2"
          name="street2"
          type="text"
          autoComplete="address-line2"
          className={[
            'w-full rounded-sm border bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2',
            tone === 'employee'
              ? 'border-slate-300 focus:border-slate-700 focus:ring-slate-300/70'
              : 'border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900',
          ].join(' ')}
          placeholder={t('auth.street_line2_placeholder', {
            defaultValue: 'Apt 4B, Suite 200, Floor 3…',
          })}
          value={street2}
          onChange={(e) => {
            setStreet2(e.target.value);
            setAddressSelected(false);
          }}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 text-sm">
          <label htmlFor="register-city" className={authLabelClass('light')}>
            {t('auth.city_label', { defaultValue: 'City' })}
          </label>
          <input
            id="register-city"
            name="city"
            type="text"
            autoComplete="address-level2"
            disabled={!countrySelected}
            className={[
              'w-full rounded-sm border bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-100',
              tone === 'employee'
                ? 'border-slate-300 focus:border-slate-700 focus:ring-slate-300/70'
                : 'border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900',
            ].join(' ')}
            value={cityInput}
            onChange={(e) => {
              setCityInput(e.target.value);
              setCitySelected(false);
              setAddressSelected(false);
            }}
            required
          />
          {citySuggestions.length > 0 ? (
            <div className="max-h-32 overflow-auto rounded-sm border border-slate-200 bg-white">
              {citySuggestions.map((c) => (
                <button
                  key={c.name}
                  type="button"
                  onClick={() => {
                    setCityInput(c.name);
                    setCitySelected(true);
                    setCitySuggestions([]);
                  }}
                  className="block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                >
                  {c.name}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {needsState ? (
          <div className="space-y-1.5 text-sm">
            <label htmlFor="register-state" className={authLabelClass('light')}>
              {t('auth.state_province_label', { defaultValue: 'State / province' })}
            </label>
            <input
              id="register-state"
              name="stateOrProvince"
              type="text"
              autoComplete="address-level1"
              className={[
                'w-full rounded-sm border bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2',
                tone === 'employee'
                  ? 'border-slate-300 focus:border-slate-700 focus:ring-slate-300/70'
                  : 'border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900',
              ].join(' ')}
              placeholder={t('auth.state_province_placeholder', {
                defaultValue: 'e.g. CA, NY, ON',
              })}
              value={stateOrProvince}
              onChange={(e) => {
                setStateOrProvince(e.target.value.toUpperCase());
                setAddressSelected(false);
              }}
              required
            />
            <p className="text-[11px] text-slate-500">
              {t('auth.state_required_hint', {
                defaultValue: 'Required for shipping to your country.',
              })}
            </p>
          </div>
        ) : (
          <div className="space-y-1.5 text-sm">
            <label htmlFor="register-state-optional" className={authLabelClass('light')}>
              {t('auth.state_province_label', { defaultValue: 'State / province' })}
              <span className="font-normal text-slate-500"> (optional)</span>
            </label>
            <input
              id="register-state-optional"
              name="stateOrProvince"
              type="text"
              autoComplete="address-level1"
              className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2 focus:border-brand-500 focus:ring-brand-400/70"
              placeholder={t('auth.state_province_placeholder', {
                defaultValue: 'e.g. CA, NY, ON',
              })}
              value={stateOrProvince}
              onChange={(e) => {
                setStateOrProvince(e.target.value);
                setAddressSelected(false);
              }}
            />
          </div>
        )}
      </div>

      <div className="space-y-1.5 text-sm sm:max-w-xs">
        <label htmlFor="register-postalCode" className={authLabelClass('light')}>
          {t('auth.postal_code_label', { defaultValue: 'Postal / ZIP code' })}
        </label>
        <input
          id="register-postalCode"
          name="postalCode"
          type="text"
          autoComplete="postal-code"
          className={[
            'w-full rounded-sm border bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2',
            tone === 'employee'
              ? 'border-slate-300 focus:border-slate-700 focus:ring-slate-300/70'
              : 'border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900',
          ].join(' ')}
          value={postalCode}
          onChange={(e) => {
            setPostalCode(e.target.value);
            setAddressSelected(false);
          }}
          required
        />
      </div>

      <div className="rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
        <span className="font-semibold">
          {t('auth.address_preview_label', { defaultValue: 'Address preview' })}
          {': '}
          {addressSelected
            ? t('auth.address_selected', { defaultValue: 'Verified suggestion applied:' })
            : t('auth.address_manual', { defaultValue: 'Manual entry:' })}
        </span>{' '}
        {street || '—'}
        {street2 ? `, ${street2}` : ''}
        <br />
        {[cityInput, stateOrProvince, postalCode].filter(Boolean).join(', ') || '—'}
        <br />
        {countryInput || '—'}
        {!addressSelected ? (
          <div className="mt-1 text-emerald-900/90">
            {t('auth.address_verify_background', {
              defaultValue:
                'We will verify this address in the background after account creation.',
            })}
          </div>
        ) : null}
      </div>

      <button
        type="submit"
        disabled={submitting || verificationSent}
        className={authPrimaryButtonClass(tone === 'employee' ? 'light' : 'light')}
      >
        {verificationSent
          ? t('auth.verification_email_sent', {
              defaultValue: 'Verification email sent',
            })
          : submitting
            ? t('auth.register_creating_state', {
                defaultValue: 'Creating account…',
              })
            : t('auth.register_button')}
      </button>
      <button
        type="button"
        onClick={() => setStep(1)}
        className={authSecondaryButtonClass('light')}
      >
        {t('common.back', { defaultValue: 'Back' })}
      </button>
        </>
      )}
      {verificationSent ? (
        <div
          role="status"
          className="rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950"
        >
          {t('auth.register_form_locked_note')}
        </div>
      ) : null}
      </fieldset>
    </form>
  );
}

export default RegisterForm;

